import { useRef, useState } from "react";
import { ExternalLink, FileText, Image as ImageIcon, X } from "lucide-react";
import { getAttachmentLink } from "@vaulzo/core/api/portfolio";
import { useAttachments } from "@vaulzo/core/hooks/usePortfolioQueries";
import { useUploadAttachment, useRemoveAttachment, ledgerErrorMessage } from "@vaulzo/core/hooks/usePortfolioMutations";

export const PROOF_KINDS = [
  { value: "debit_advice", label: "Debit advice" },
  { value: "swift_advice", label: "SWIFT advice" },
  { value: "trade_confirmation", label: "Trade confirmation" },
  { value: "transfer_statement", label: "Transfer statement" },
  { value: "other", label: "Other" },
];

const DEFAULT_KIND = {
  convert: "debit_advice",
  buy: "trade_confirmation",
  sell: "trade_confirmation",
  transfer: "transfer_statement",
};

export const defaultProofKind = (type) => DEFAULT_KIND[type] || "other";

const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.heic,.heif,application/pdf,image/png,image/jpeg,image/heic,image/heif";
const ALLOWED = /\.(pdf|png|jpe?g|heic|heif)$/i;

const kindLabel = (kind) => PROOF_KINDS.find((k) => k.value === kind)?.label || "Other";
const sizeLabel = (bytes) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Quick client-side check; the server sniffs the bytes and has the final say. */
export function proofProblem(file) {
  if (!ALLOWED.test(file.name) && !/^(application\/pdf|image\/(png|jpeg|heic|heif))$/.test(file.type)) {
    return `${file.name}: only PDF, PNG, JPEG or HEIC files can be attached`;
  }
  if (file.size > MAX_BYTES) return `${file.name}: files can be up to 10 MB`;
  return null;
}

/**
 * Proof files on an entry: bank advices, trade confirmations, screenshots.
 * On a saved entry files upload as soon as they're picked; on a new one
 * they wait in `queued` and the entry modal uploads them after saving.
 */
export default function ProofFiles({ assetId, entry, entryType, queued, setQueued }) {
  const { data: saved = [], isLoading } = useAttachments(assetId, entry?.id);
  const upload = useUploadAttachment();
  const remove = useRemoveAttachment();
  // The type follows the entry's until it's picked by hand.
  const [kindChoice, setKindChoice] = useState(null);
  const kind = kindChoice || defaultProofKind(entryType);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState([]); // names in flight
  const [confirming, setConfirming] = useState(null); // id or queued index
  const [dragOver, setDragOver] = useState(false);
  const input = useRef(null);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setError("");
    const problems = files.map(proofProblem).filter(Boolean);
    const ok = files.filter((f) => !proofProblem(f));
    if (problems.length) setError(problems.join(" · "));
    if (!entry) {
      setQueued((q) => [...q, ...ok.map((file) => ({ file, kind }))]);
      return;
    }
    for (const file of ok) {
      setUploading((u) => [...u, file.name]);
      try {
        await upload.mutateAsync({ assetId, txId: entry.id, file, kind });
      } catch (err) {
        setError((e) => [e, `${file.name}: ${ledgerErrorMessage(err, "could not upload")}`].filter(Boolean).join(" · "));
      } finally {
        setUploading((u) => u.filter((n) => n !== file.name));
      }
    }
  };

  // The tab opens synchronously (so it isn't blocked as a popup), then
  // gets the short-lived link.
  const open = async (att) => {
    const tab = window.open("about:blank", "_blank");
    try {
      const url = await getAttachmentLink(assetId, entry.id, att.id);
      if (tab) tab.location.href = url;
      else window.location.href = url;
    } catch (err) {
      if (tab) tab.close();
      setError(ledgerErrorMessage(err, "Could not open the file"));
    }
  };

  const removeSaved = async (att) => {
    if (confirming !== att.id) return setConfirming(att.id);
    setConfirming(null);
    try {
      await remove.mutateAsync({ assetId, txId: entry.id, id: att.id });
    } catch (err) {
      setError(ledgerErrorMessage(err, "Could not remove the file"));
    }
  };

  const rows = entry
    ? saved.map((att) => ({
        key: att.id,
        name: att.filename,
        meta: `${kindLabel(att.kind)} · ${sizeLabel(att.size)}`,
        image: att.content_type.startsWith("image/"),
        onOpen: () => open(att),
        onRemove: () => removeSaved(att),
        confirm: confirming === att.id,
      }))
    : queued.map((q, i) => ({
        key: `${q.file.name}-${i}`,
        name: q.file.name,
        meta: `${kindLabel(q.kind)} · ${sizeLabel(q.file.size)} · attaches on save`,
        image: q.file.type.startsWith("image/"),
        onRemove: () => setQueued((list) => list.filter((_, j) => j !== i)),
      }));

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[12px] font-semibold text-pf-soft">Proof</span>
        <select
          value={kind}
          onChange={(e) => setKindChoice(e.target.value)}
          aria-label="Type of the next file"
          className="rounded-md border border-pf-input bg-white px-2 py-1 text-[12px] text-pf-body outline-none focus:border-pf-focus"
        >
          {PROOF_KINDS.map((k) => (
            <option key={k.value} value={k.value}>
              {k.label}
            </option>
          ))}
        </select>
      </div>

      {rows.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-pf-line">
          {rows.map((row) => (
            <div key={row.key} className="flex items-center gap-[10px] border-b border-pf-divider px-3 py-2 last:border-b-0">
              {row.image ? (
                <ImageIcon className="h-4 w-4 flex-none text-pf-muted" strokeWidth={1.8} />
              ) : (
                <FileText className="h-4 w-4 flex-none text-pf-muted" strokeWidth={1.8} />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] text-pf-ink">{row.name}</span>
                <span className="block truncate text-[11px] text-pf-faint">{row.meta}</span>
              </span>
              {row.onOpen && (
                <button
                  type="button"
                  onClick={row.onOpen}
                  aria-label={`Open ${row.name}`}
                  className="flex h-7 w-7 items-center justify-center rounded-md text-pf-muted hover:bg-pf-subtle hover:text-pf-ink"
                >
                  <ExternalLink className="h-[14px] w-[14px]" strokeWidth={1.8} />
                </button>
              )}
              <button
                type="button"
                onClick={row.onRemove}
                aria-label={`Remove ${row.name}`}
                className={`flex h-7 items-center justify-center rounded-md px-[6px] text-[12px] font-semibold ${
                  row.confirm ? "bg-pf-neg-tint text-pf-neg" : "text-pf-muted hover:bg-pf-subtle hover:text-pf-neg"
                }`}
              >
                {row.confirm ? "Remove?" : <X className="h-4 w-4" strokeWidth={1.8} />}
              </button>
            </div>
          ))}
        </div>
      )}

      {uploading.length > 0 && <div className="text-[12px] text-pf-faint">Uploading {uploading.join(", ")}…</div>}
      {entry && isLoading && <div className="text-[12px] text-pf-faint">Loading files…</div>}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
        className={`flex flex-col items-center gap-1 rounded-lg border border-dashed px-3 py-3 text-center transition-colors ${
          dragOver ? "border-pf-focus bg-pf-hover" : "border-pf-input"
        }`}
      >
        <button
          type="button"
          onClick={() => input.current?.click()}
          className="text-[12px] font-semibold text-pf-accent hover:text-pf-accent-hover"
        >
          + Attach file
        </button>
        <span className="text-[11px] text-pf-faint">or drop it here · PDF, PNG, JPEG or HEIC up to 10 MB</span>
        <input
          ref={input}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {error && <div className="text-[12px] text-pf-neg">{error}</div>}
    </div>
  );
}
